import type { ChangeEvent } from "react";
/* eslint-disable react-refresh/only-export-components */

/**
 * Structured address model. Addresses are stored on `customers` as jsonb in
 * this shape (and snapshotted onto sales orders at order time), so they are
 * usable for fulfilment/rates rather than a single free-text blob.
 */
export interface AddressValues {
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export const emptyAddress = (): AddressValues => ({
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
});

export const addressFromJson = (a: Record<string, string> | null | undefined): AddressValues => ({
  address_line1: a?.address_line1 ?? a?.address ?? "",
  address_line2: a?.address_line2 ?? "",
  city: a?.city ?? "",
  state: a?.state ?? "",
  postal_code: a?.postal_code ?? "",
  country: a?.country ?? "",
});

export const addressToJson = (v: AddressValues): Record<string, string> | null => {
  const hasValue = Object.values(v).some((s) => s.trim() !== "");
  if (!hasValue) return null;
  return {
    address_line1: v.address_line1.trim(),
    address_line2: v.address_line2.trim(),
    city: v.city.trim(),
    state: v.state.trim(),
    postal_code: v.postal_code.trim(),
    country: v.country.trim(),
  };
};

export const formatAddress = (a: Record<string, string> | null | undefined): string => {
  if (!a) return "";
  const line1 = a.address_line1 ?? a.address ?? "";
  const line2 = a.address_line2 ?? "";
  const city = a.city ?? "";
  const state = a.state ?? "";
  const postal = a.postal_code ?? "";
  const country = a.country ?? "";
  const firstLine = [line1, line2].filter(Boolean).join(", ");
  const secondLine = [[city, state].filter(Boolean).join(", "), postal].filter(Boolean).join(" ");
  return [firstLine, secondLine, country].filter(Boolean).join(", ");
};

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export default function AddressFields({
  label,
  value,
  onChange,
  idPrefix,
}: {
  label: string;
  value: AddressValues;
  onChange: (v: AddressValues) => void;
  idPrefix?: string;
}) {
  const set =
    (k: keyof AddressValues) =>
    (e: ChangeEvent<HTMLInputElement>) =>
      onChange({ ...value, [k]: e.target.value });
  const idFor = (field: keyof AddressValues) => (idPrefix ? `${idPrefix}-${field}` : undefined);

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</p>
      <div>
        <label className={labelCls} htmlFor={idFor("address_line1")}>Address line 1</label>
        <input id={idFor("address_line1")} value={value.address_line1} onChange={set("address_line1")} className={inputCls} placeholder="120 Pitt Street" />
      </div>
      <div>
        <label className={labelCls} htmlFor={idFor("address_line2")}>Address line 2</label>
        <input id={idFor("address_line2")} value={value.address_line2} onChange={set("address_line2")} className={inputCls} placeholder="Level 2" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls} htmlFor={idFor("city")}>City</label>
          <input id={idFor("city")} value={value.city} onChange={set("city")} className={inputCls} placeholder="Sydney" />
        </div>
        <div>
          <label className={labelCls} htmlFor={idFor("state")}>State / Province</label>
          <input id={idFor("state")} value={value.state} onChange={set("state")} className={inputCls} placeholder="NSW" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls} htmlFor={idFor("postal_code")}>Postal code</label>
          <input id={idFor("postal_code")} value={value.postal_code} onChange={set("postal_code")} className={inputCls} placeholder="2000" />
        </div>
        <div>
          <label className={labelCls} htmlFor={idFor("country")}>Country</label>
          <input id={idFor("country")} value={value.country} onChange={set("country")} className={inputCls} placeholder="AU" />
        </div>
      </div>
    </div>
  );
}
